/**
 * showTypes — SOURCE UNIQUE des types d'événement et de leur profil (2026-10-17).
 *
 * POURQUOI : les 8 onglets de la page show et le bloc venue s'affichaient pour
 * TOUS les types — un travel day ou un day off n'ont ni venue ni rider technique.
 * Chaque type déclare ici un PROFIL : `needsVenue` (masque le bloc venue sans
 * jamais détruire de données) et `tabs` (les onglets qui ont du sens).
 *
 * RÈGLE DE SÉCURITÉ : on MASQUE des champs, on ne détruit jamais de données —
 * changer le type d'un show ne supprime rien ; remettre le type réaffiche tout.
 *
 * Consumé par : ShowDetailPage (venue block + onglets + placeholder du nom),
 * ShowsImportReviewPage (liste des types), et les tests.
 */

export const EVENT_TYPE_OPTIONS = [
  'show', 'travel', 'soundcheck', 'rehearsal', 'festival', 'djset',
  'promo', 'studio', 'off',
] as const;

export type ShowEventType = (typeof EVENT_TYPE_OPTIONS)[number] | 'other' | '';

/** Les onglets de la page show, dans l'ordre d'affichage. */
export type ShowTab =
  | 'day' | 'logistics' | 'tech' | 'setlist' | 'checklist'
  | 'files' | 'advancing' | 'finance';

/** Profils par type — tout type inconnu/vide (`other`, `''`) reçoit le profil COMPLET. */
export interface ShowTypeProfile {
  /** Le bloc venue (sélecteur + recherche en ligne) a du sens pour ce type. */
  needsVenue: boolean;
  /** Onglets affichés pour ce type (les autres sont masqués, données intactes). */
  tabs: ShowTab[];
  /**
   * Le bloc PERSONNES : qui le show concerne, et comment le nommer.
   * performers = « qui joue » (hint rider + itinéraire) · travelers = « qui voyage » ·
   * involved = « qui est impliqué » (soundcheck/promo) · concerned = « qui est concerné ».
   */
  people: 'performers' | 'travelers' | 'involved' | 'concerned';
  /** Le sélecteur de setlist (header) n'a de sens que pour les types qui jouent. */
  hideSetlist?: boolean;
  /**
   * Blocs/champs de la DAY SHEET (ItinerarySection + DaysheetViewer + PDF) masqués
   * pour ce type — doors/running order/stage name n'ont aucun sens sur un travel day.
   */
  daySheetHidden?: Array<'doors' | 'runningOrder' | 'stageName' | 'guests' | 'artistPass' | 'passport'>;
  /** Placeholder du nom adapté au type (appliqué seulement si le nom est vide). */
  namePhKey?: string;
}

const ALL_TABS: ShowTab[] = ['day', 'logistics', 'tech', 'setlist', 'checklist', 'files', 'advancing', 'finance'];

// Spectacle : profil COMPLET (festival/djset sont des variantes de show).
const PERFORMANCE: ShowTypeProfile = { needsVenue: true, tabs: ALL_TABS, people: 'performers' };
// Journée sans spectacle : pas de venue, pas de rider, pas de setlist/advancing/finance.
const NO_SHOW: ShowTypeProfile = {
  needsVenue: false, tabs: ['day', 'logistics', 'checklist', 'files'],
  people: 'travelers', hideSetlist: true,
  daySheetHidden: ['doors', 'runningOrder', 'stageName', 'guests'],
};

const PROFILES: Record<string, ShowTypeProfile> = {
  show: PERFORMANCE,
  festival: PERFORMANCE,
  djset: PERFORMANCE,
  travel: { ...NO_SHOW, namePhKey: 'namePhTravel' },
  off: {
    needsVenue: false, tabs: ['day', 'checklist', 'files'],
    people: 'concerned', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'guests'],
    namePhKey: 'namePhOff',
  },
  soundcheck: {
    needsVenue: true, tabs: ['day', 'logistics', 'files', 'checklist'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName'],
  },
  rehearsal: {
    needsVenue: true, tabs: ['day', 'logistics', 'files', 'checklist'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName'],
  },
  // Promo / press day : venue souvent oui (studio radio, salle d'interview) —
  // mais jamais de rider ni de setlist ni d'advancing ni de finance.
  promo: {
    needsVenue: true, tabs: ['day', 'logistics', 'checklist', 'files'],
    people: 'involved', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName'],
  },
  // Writing / recording day : comme un day off avec un objectif.
  studio: {
    needsVenue: false, tabs: ['day', 'logistics', 'checklist', 'files'],
    people: 'concerned', hideSetlist: true,
    daySheetHidden: ['doors', 'runningOrder', 'stageName', 'guests'],
    namePhKey: 'namePhStudio',
  },
};

/** Profil d'un type — TOUJOURS défini (type inconnu/vide = profil COMPLET, rétro-compatible). */
export function showTypeProfile(eventType: string | null | undefined): ShowTypeProfile {
  return PROFILES[eventType ?? ''] ?? PERFORMANCE;
}

/**
 * Nom AUTO-GÉNÉRÉ d'un travel day (2026-10-17, user) : `Travel Day (Genève) - (Milan)`.
 * ÉDITABLE : l'auto-nom ne s'applique que si le nom est VIDE ou correspond DÉJÀ au pattern
 * auto (un nom tapé à la main n'est jamais écrasé — on détecte le pattern via
 * `isAutoTravelName`).
 */
export function autoTravelName(fromCity?: string | null, toCity?: string | null): string {
  const from = (fromCity || '').trim();
  const to = (toCity || '').trim();
  if (from && to) return `Travel Day (${from}) - (${to})`;
  if (from) return `Travel Day (${from})`;
  return 'Travel Day';
}

const AUTO_TRAVEL_RE = /^Travel Day( \()?/i;
/** Le nom actuel est-il un auto-nom (safe à régénérer) ? Un nom manuel n'est jamais touché. */
export function isAutoTravelName(name?: string | null): boolean {
  const n = (name || '').trim();
  if (!n) return true; // vide → l'auto-nom peut s'appliquer
  return AUTO_TRAVEL_RE.test(n);
}

/**
 * Noms AUTO par type (2026-10-17, user) : travel = Travel Day (from) - (to) ;
 * soundcheck / rehearsal / promo = `{Type} — {venue}` quand la venue est connue.
 */
export function autoShowName(
  eventType: string | null | undefined,
  ctx: { fromCity?: string | null; toCity?: string | null; venueName?: string | null },
): string | null {
  const type = (eventType || '').toLowerCase();
  if (type === 'travel') return autoTravelName(ctx.fromCity, ctx.toCity);
  const v = (ctx.venueName || '').trim();
  if (!v) return null;
  if (type === 'soundcheck') return `Soundcheck — ${v}`;
  if (type === 'rehearsal') return `Rehearsal — ${v}`;
  if (type === 'promo') return `Promo — ${v}`;
  return null;
}

/** Le nom est-il un AUTO-nom (safe à régénérer) ? Un nom manuel n'est jamais touché. */
export function isAutoShowName(name?: string | null): boolean {
  const n = (name || '').trim();
  if (!n) return true;
  if (AUTO_TRAVEL_RE.test(n)) return true;
  return [/^Soundcheck — /i, /^Rehearsal — /i, /^Promo — /i].some((re) => re.test(n));
}
